// react
import { useContext, useEffect, useMemo, useState } from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { Account, SpendPlan } from '../../../../@types';

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// services
import { fetchSpendPlans } from '../../../../services/api';

// mui
import { Box, Button, Tooltip } from '@mui/material';

// icons
import {
	AccountBalance as IconAccount,
	AttachMoney as IconMoney,
	Savings as IconEarmark,
	TrendingUp as IconTrending,
	UnfoldLess as IconCollapseAll,
	UnfoldMore as IconExpandAll,
} from '@mui/icons-material';

// components

// data
import { COLORS } from '../../../../data';

// v2 components
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { StatCardRow } from '../../../ui/StatCardRow/StatCardRow';

// local
import { FundingEarmarksDataTable } from './FundingEarmarksDataTable';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { getProjectReportingCurrency } from '../../../../utils/fxRate';

// Types

interface SpendPlanVoucherSummary {
	pending: number;
	pendingCount: number;
	redeemed: number;
	redeemedCount: number;
}

interface AccountEarmark {
	account: Account;
	balance: number;
	currency: string;
	spendPlans: SpendPlan[];
	totalEarmarked: number;
	totalRemaining: number;
	vouchersByPlan: Record<number, SpendPlanVoucherSummary>;
	vouchersPending: number;
	vouchersPendingCount: number;
	vouchersRedeemed: number;
	vouchersRedeemedCount: number;
}

// Component

export const FundingEarmarks = () => {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { accounts, vouchers, loading: ctxLoading } = useContext(ContextSpendPlan);

	// Project reporting currency (USD for IBRD/IDA, EUR for some donors) drives
	// the labels on the cross-account roll-up cards. Individual account rows
	// still display in their own balance currency.
	const reportingCurrency = getProjectReportingCurrency(project) || 'USD';

	// URL-driven expand state
	const expandAllParam = searchParams.get('expanded') === 'all';
	const [expandAll, setExpandAll] = useState<boolean | undefined>(undefined);

	// URL-driven sort
	const sortOrderBy = searchParams.get('orderBy') || 'account';
	const sortOrder = (searchParams.get('order') || 'ASC') as 'ASC' | 'DESC';

	const handleSortChange = (col: string, dir: 'ASC' | 'DESC') => {
		setSearchParams((params) => {
			params.set('orderBy', col);
			params.set('order', dir);
			return params;
		});
	};

	const toggleExpandAll = () => {
		const newVal = !expandAllParam;
		setSearchParams((params) => {
			if (newVal) params.set('expanded', 'all');
			else params.delete('expanded');
			return params;
		});
		setExpandAll(newVal);
	};

	// state
	const [spendPlans, setSpendPlans] = useState<SpendPlan[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchValue, setSearchValue] = useState('');

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// Fetch spend plans to map earmarkings
	useEffect(() => {
		const loadSpendPlans = async () => {
			if (!project?.id || !accessToken || !idToken) return;

			setIsLoading(true);
			try {
				const result = await fetchSpendPlans(
					{ accessToken, idToken },
					{
						project: project.id,
						limit: 100,
						populate: 'account',
					}
				);

				if (result.error) {
					setBanner({
						message: result.error.message || t('spendPlan.fundingEarmarks.failedToLoad'),
						severity: 'error',
					});
					return;
				}

				setSpendPlans(result.data || []);
			} catch {
				setBanner({
					message: t('spendPlan.fundingEarmarks.failedToLoad'),
					severity: 'error',
				});
			} finally {
				setIsLoading(false);
			}
		};

		loadSpendPlans();
	}, [project?.id, accessToken, idToken, setBanner]);

	// Build earmark data: group spend plans by account
	const accountEarmarks: AccountEarmark[] = useMemo(() => {
		if (!accounts.length) return [];

		// Pre-aggregate vouchers by spendPlanId
		const voucherByPlan = new Map<number, SpendPlanVoucherSummary>();
		vouchers.forEach((v) => {
			if (v.spendPlanId == null || v.status === 'CANCELLED') return;
			const entry = voucherByPlan.get(v.spendPlanId) || {
				pending: 0,
				pendingCount: 0,
				redeemed: 0,
				redeemedCount: 0,
			};
			const amount = v.amount || 0;
			if (v.status === 'REDEEMED') {
				entry.redeemed += amount;
				entry.redeemedCount += 1;
			} else {
				// GENERATED, READY, VERIFIED -> pending
				entry.pending += amount;
				entry.pendingCount += 1;
			}
			voucherByPlan.set(v.spendPlanId, entry);
		});

		return accounts.map((account) => {
			// Find all spend plans linked to this account
			const linkedPlans = spendPlans.filter((sp) => {
				const spAccountId =
					typeof sp.account === 'object' && sp.account ? sp.account.id : sp.account;
				return spAccountId === account.id;
			});

			// Calculate total earmarked from spend plans
			const totalEarmarked = linkedPlans.reduce((sum, sp) => sum + (sp.totalPlanned || 0), 0);

			// Get account balance (primary balance or first balance entry)
			const primaryBalance = account.balances?.[0];
			const balance = primaryBalance ? primaryBalance.balance : 0;
			const currency = primaryBalance?.currency || account.currency || 'USD';

			// Aggregate voucher amounts for this account's spend plans
			const vouchersByPlan: Record<number, SpendPlanVoucherSummary> = {};
			let vouchersPending = 0;
			let vouchersPendingCount = 0;
			let vouchersRedeemed = 0;
			let vouchersRedeemedCount = 0;
			linkedPlans.forEach((sp) => {
				const summary = voucherByPlan.get(sp.id) || {
					pending: 0,
					pendingCount: 0,
					redeemed: 0,
					redeemedCount: 0,
				};
				vouchersByPlan[sp.id] = summary;
				vouchersPending += summary.pending;
				vouchersPendingCount += summary.pendingCount;
				vouchersRedeemed += summary.redeemed;
				vouchersRedeemedCount += summary.redeemedCount;
			});

			return {
				account,
				balance,
				currency,
				spendPlans: linkedPlans,
				totalEarmarked,
				totalRemaining: balance - totalEarmarked,
				vouchersByPlan,
				vouchersPending,
				vouchersPendingCount,
				vouchersRedeemed,
				vouchersRedeemedCount,
			};
		});
	}, [accounts, spendPlans, vouchers]);

	// Client-side search filtering
	const filteredEarmarks = useMemo(() => {
		if (!searchValue.trim()) return accountEarmarks;
		const term = searchValue.toLowerCase();
		return accountEarmarks.filter(
			(ae) =>
				ae.account.name?.toLowerCase().includes(term) ||
				ae.account.num?.toLowerCase().includes(term) ||
				ae.account.institution?.toLowerCase().includes(term)
		);
	}, [accountEarmarks, searchValue]);

	// Client-side sorting
	const sortedEarmarks = useMemo(() => {
		const sorted = [...filteredEarmarks];
		const dir = sortOrder === 'ASC' ? 1 : -1;

		sorted.sort((a, b) => {
			switch (sortOrderBy) {
				case 'account': {
					const nameA = (a.account.name || a.account.num || '').toLowerCase();
					const nameB = (b.account.name || b.account.num || '').toLowerCase();
					return dir * nameA.localeCompare(nameB);
				}
				case 'balance':
					return dir * ((a.balance || 0) - (b.balance || 0));
				case 'earmarked':
					return dir * ((a.totalEarmarked || 0) - (b.totalEarmarked || 0));
				case 'vouchersPending':
					return dir * ((a.vouchersPending || 0) - (b.vouchersPending || 0));
				case 'vouchersRedeemed':
					return dir * ((a.vouchersRedeemed || 0) - (b.vouchersRedeemed || 0));
				case 'remaining':
					return dir * ((a.totalRemaining || 0) - (b.totalRemaining || 0));
				case 'utilization': {
					const utilA = a.balance > 0 ? a.totalEarmarked / a.balance : 0;
					const utilB = b.balance > 0 ? b.totalEarmarked / b.balance : 0;
					return dir * (utilA - utilB);
				}
				default:
					return 0;
			}
		});

		return sorted;
	}, [filteredEarmarks, sortOrderBy, sortOrder]);

	// Summary stats — aggregated per currency. Accounts can be in different
	// currencies (USD designated account + PHP local account, etc.), so we
	// group balances/earmarks by currency. The reporting-currency totals are
	// the headline; any other currency totals are shown on a second line under
	// each card so we never silently add dollars and pesos.
	const summary = useMemo(() => {
		const byCurrency = new Map<string, { balance: number; earmarked: number; accounts: number }>();
		accountEarmarks.forEach((ae) => {
			const entry = byCurrency.get(ae.currency) || { balance: 0, earmarked: 0, accounts: 0 };
			entry.balance += ae.balance;
			entry.earmarked += ae.totalEarmarked;
			if (ae.spendPlans.length > 0) entry.accounts += 1;
			byCurrency.set(ae.currency, entry);
		});

		const reporting = byCurrency.get(reportingCurrency) || {
			balance: 0,
			earmarked: 0,
			accounts: 0,
		};
		const others = Array.from(byCurrency.entries())
			.filter(([c]) => c !== reportingCurrency)
			.sort((a, b) => a[0].localeCompare(b[0]));

		// Format a one-line summary of the non-reporting currency buckets, e.g.
		// "+ PHP ₱10,000.00". Multiple currencies stack with " · " separators.
		const fmtOthers = (key: 'balance' | 'earmarked') =>
			others
				.filter(([, v]) => v[key] !== 0)
				.map(([c, v]) => `+ ${formatCurrency({ value: v[key], currency: c })}`)
				.join(' · ') || undefined;

		const otherBalanceLine = fmtOthers('balance');
		const otherEarmarkedLine = fmtOthers('earmarked');
		const otherUnearmarkedLine =
			others
				.map(([c, v]) => ({ c, v: v.balance - v.earmarked }))
				.filter((x) => x.v !== 0)
				.map((x) => `+ ${formatCurrency({ value: x.v, currency: x.c })}`)
				.join(' · ') || undefined;

		// Utilization stays a pure reporting-currency ratio so the percentage is
		// always math-correct (no implicit FX). If there is no reporting balance
		// to divide into, the card falls back to 0%.
		const utilization =
			reporting.balance > 0 ? Math.round((reporting.earmarked / reporting.balance) * 100) : 0;

		return {
			reportingBalance: reporting.balance,
			reportingEarmarked: reporting.earmarked,
			reportingUnearmarked: reporting.balance - reporting.earmarked,
			reportingAccountsWithEarmarks: reporting.accounts,
			otherBalanceLine,
			otherEarmarkedLine,
			otherUnearmarkedLine,
			utilization,
		};
	}, [accountEarmarks, reportingCurrency]);

	const loading = isLoading || ctxLoading.accounts;

	return (
		<Box>
			{/* Header */}
			<HeaderListingPage
				icon={IconEarmark}
				title={t('spendPlan.fundingEarmarks')}
					subtitle={t('spendPlan.fundingEarmarks.subtitle')}
				variant="financial"
			/>

			{/* Content */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{/* Summary Cards */}
				<StatCardRow
					loading={loading}
					cards={[
						{
							icon: IconAccount,
							label: t('spendPlan.fundingEarmarks.totalAccountBalance'),
							value: formatCurrency({
								value: summary.reportingBalance,
								currency: reportingCurrency,
							}),
							valueLocal: summary.otherBalanceLine,
							color: COLORS.primary.main,
						},
						{
							icon: IconEarmark,
							label: t('spendPlan.fundingEarmarks.totalEarmarked'),
							value: formatCurrency({
								value: summary.reportingEarmarked,
								currency: reportingCurrency,
							}),
							secondary: t('spendPlan.fundingEarmarks.inAccounts', { count: summary.reportingAccountsWithEarmarks }),
							valueLocal: summary.otherEarmarkedLine,
							color: COLORS.success.main,
						},
						{
							icon: IconMoney,
							label: t('spendPlan.fundingEarmarks.unearmarked'),
							value: formatCurrency({
								value: summary.reportingUnearmarked,
								currency: reportingCurrency,
							}),
							valueLocal: summary.otherUnearmarkedLine,
							color: summary.reportingUnearmarked < 0 ? COLORS.error.main : COLORS.info.main,
						},
						{
							icon: IconTrending,
							label: t('utilization'),
							value: `${summary.utilization}%`,
							color: COLORS.warning.main,
						},
					]}
				/>

				{/* DataTable with expandable rows */}
				<Box sx={{ mt: 3 }}>
					<FundingEarmarksDataTable
						data={sortedEarmarks}
						isLoading={loading}
						searchValue={searchValue}
						onSearchChange={setSearchValue}
						expandAll={expandAll}
						orderBy={sortOrderBy}
						order={sortOrder}
						onSortChange={handleSortChange}
						headerActions={
							<Tooltip title={expandAllParam ? t('collapseAll') : t('expandAll')}>
								<Button
									size="small"
									onClick={toggleExpandAll}
									startIcon={expandAllParam ? <IconCollapseAll /> : <IconExpandAll />}
									sx={{
										textTransform: 'none',
										fontWeight: 600,
										color: '#6b7280',
										whiteSpace: 'nowrap',
									}}>
									{expandAllParam ? t('collapseAll') : t('expandAll')}
								</Button>
							</Tooltip>
						}
					/>
				</Box>
			</Box>
		</Box>
	);
};
